import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, RotateCcw } from "lucide-react";
import { Button } from "@/components/site/Button";
import { track, trackCta } from "@/lib/analytics";
import {
  CHANNELS,
  PACKAGES,
  PERIODS,
  calculateQuote,
  formatCurrency,
  recommendPackageForChannels,
  type ChannelId,
  type PackageId,
  type Period,
} from "@/lib/pricing";

// ---------------------------------------------------------------------------
// Interaktiv 6-stegs prisberäknare (Prioritet A — punkt 7).
// Sex frågor → rekommenderar Pulse (med uppskattat månadspris), Prime eller
// Pilot. All logik körs i klienten och läser priser från src/lib/pricing.ts
// så att beräknaren aldrig driftar isär med tier-kortet.
// ---------------------------------------------------------------------------

type Goal = "loop" | "campaign" | "test";
type Frequency = "weekly" | "monthly" | "quarterly" | "once";
type Priority = "predictable" | "speed" | "premium";

interface Answers {
  goal: Goal | null;
  frequency: Frequency | null;
  channels: ChannelId[];
  clips: number;
  period: Period;
  priority: Priority | null;
}

type Recommendation =
  | { track: "pulse"; packageId: PackageId; monthly: number }
  | { track: "prime" }
  | { track: "pilot" };

const TOTAL_STEPS = 6;

const GOALS: { id: Goal; label: string; help: string }[] = [
  { id: "loop", label: "Löpande innehåll", help: "Vi behöver film varje månad, jämn takt." },
  { id: "campaign", label: "En specifik produktion", help: "En kampanj, brandfilm eller större film." },
  { id: "test", label: "Testa er först", help: "Vi vill se hur ni jobbar innan vi binder oss." },
];

const FREQUENCIES: { id: Frequency; label: string; help: string }[] = [
  { id: "weekly", label: "Varje vecka", help: "Högt tempo, flera klipp i veckan." },
  { id: "monthly", label: "Varje månad", help: "Ett stadigt månadsflöde." },
  { id: "quarterly", label: "Per kvartal", help: "Innehåll i block några gånger per år." },
  { id: "once", label: "Engångsbehov", help: "En produktion just nu." },
];

const PRIORITIES: { id: Priority; label: string; help: string }[] = [
  { id: "predictable", label: "Förutsägbar budget", help: "Fast pris, inga offertspel." },
  { id: "speed", label: "Snabb start", help: "Första klippet så fort som möjligt." },
  { id: "premium", label: "Högsta kvalitet", help: "Senior crew, inga genvägar." },
];

function recommend(a: Answers): Recommendation {
  // Lågtröskel: vill testa → Pilot.
  if (a.goal === "test") return { track: "pilot" };
  // Engångsproduktion / kampanj → Prime.
  if (a.goal === "campaign" || a.frequency === "once") return { track: "prime" };

  // Annars Pulse — välj nivå utifrån volym och kanalbredd.
  const byChannels = recommendPackageForChannels(a.channels.length ? a.channels : ["webb"]);
  const byVolume: PackageId = a.clips <= 2 ? "lite" : a.clips <= 4 ? "core" : "pro";
  const order: PackageId[] = ["lite", "core", "pro"];
  const packageId = order[Math.max(order.indexOf(byChannels), order.indexOf(byVolume))];

  const pkg = PACKAGES[packageId];
  const extraClips = Math.max(0, a.clips - pkg.clipsIncluded);
  const quote = calculateQuote({
    packageId,
    extraClips,
    extraFilmdays: 0,
    addons: [],
    period: a.period,
  });
  return { track: "pulse", packageId, monthly: Math.round(quote.monthlyAverage) };
}

export function PriceFinder() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [a, setA] = useState<Answers>({
    goal: null,
    frequency: null,
    channels: [],
    clips: 4,
    period: "kvartal",
    priority: null,
  });

  useEffect(() => {
    track("price_finder_view", { location: "film-pris-kalkyl" });
  }, []);

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0:
        return a.goal !== null;
      case 1:
        return a.frequency !== null;
      case 2:
        return a.channels.length > 0;
      case 3:
        return a.clips > 0;
      case 4:
        return true;
      case 5:
        return a.priority !== null;
      default:
        return false;
    }
  }, [step, a]);

  const rec = useMemo(() => (done ? recommend(a) : null), [done, a]);

  function next() {
    if (!canAdvance) return;
    if (step === TOTAL_STEPS - 1) {
      const result = recommend(a);
      track("price_finder_completed", {
        location: "film-pris-kalkyl",
        recommendation: result.track,
        package: result.track === "pulse" ? result.packageId : undefined,
        monthly: result.track === "pulse" ? result.monthly : undefined,
        goal: a.goal ?? undefined,
        frequency: a.frequency ?? undefined,
        clips: a.clips,
      });
      setDone(true);
      return;
    }
    track("price_finder_step", { location: "film-pris-kalkyl", step: step + 1 });
    setStep((s) => s + 1);
  }

  function back() {
    if (step === 0) return;
    setStep((s) => s - 1);
  }

  function reset() {
    setDone(false);
    setStep(0);
    setA({ goal: null, frequency: null, channels: [], clips: 4, period: "kvartal", priority: null });
  }

  function toggleChannel(id: ChannelId) {
    setA((prev) => ({
      ...prev,
      channels: prev.channels.includes(id)
        ? prev.channels.filter((c) => c !== id)
        : [...prev.channels, id],
    }));
  }

  if (done && rec) {
    return <Result rec={rec} onReset={reset} />;
  }

  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-white/10 bg-surface/60 p-6 backdrop-blur-md md:p-8">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-mint">
          Steg {step + 1} av {TOTAL_STEPS}
        </div>
        <div className="text-xs text-muted-foreground">Prisberäknare</div>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-mint transition-all duration-300"
          style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
        />
      </div>

      <div className="mt-8 min-h-[16rem]">
        {step === 0 && (
          <Choice
            heading="Vad är ert huvudmål?"
            options={GOALS}
            value={a.goal}
            onSelect={(v) => setA((p) => ({ ...p, goal: v }))}
          />
        )}
        {step === 1 && (
          <Choice
            heading="Hur ofta behöver ni ny film?"
            options={FREQUENCIES}
            value={a.frequency}
            onSelect={(v) => setA((p) => ({ ...p, frequency: v }))}
          />
        )}
        {step === 2 && (
          <div>
            <h3 className="text-xl font-semibold md:text-2xl">Vilka kanaler ska filmen leva i?</h3>
            <p className="mt-2 text-sm text-muted-foreground">Välj alla som gäller.</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {CHANNELS.map((c) => {
                const active = a.channels.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChannel(c.id)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${
                      active
                        ? "border-mint bg-mint/15 text-mint"
                        : "border-white/15 bg-background/40 text-foreground/80 hover:border-white/35"
                    }`}
                  >
                    {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {step === 3 && (
          <div>
            <h3 className="text-xl font-semibold md:text-2xl">Hur många klipp per månad?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Ungefär — vi finjusterar på mötet.
            </p>
            <div className="mt-8 text-center">
              <div className="text-5xl font-semibold text-mint">{a.clips}</div>
              <div className="text-sm text-muted-foreground">klipp / månad</div>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={a.clips}
              onChange={(e) => setA((p) => ({ ...p, clips: Number(e.target.value) }))}
              className="mt-6 w-full accent-mint"
              aria-label="Klipp per månad"
            />
            <div className="mt-1 flex justify-between text-xs text-muted-foreground">
              <span>1</span>
              <span>10+</span>
            </div>
          </div>
        )}
        {step === 4 && (
          <div>
            <h3 className="text-xl font-semibold md:text-2xl">Hur vill ni helst binda er?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Längre bindning ger lägre månadspris.
            </p>
            <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {PERIODS.map((p) => {
                const active = a.period === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setA((prev) => ({ ...prev, period: p.id }))}
                    className={`rounded-2xl border px-4 py-3.5 text-left text-sm font-medium transition-colors ${
                      active
                        ? "border-mint bg-mint/15 text-mint"
                        : "border-white/15 bg-background/40 text-foreground/80 hover:border-white/35"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {step === 5 && (
          <Choice
            heading="Vad väger tyngst för er?"
            options={PRIORITIES}
            value={a.priority}
            onSelect={(v) => setA((p) => ({ ...p, priority: v }))}
          />
        )}
      </div>

      {/* Nav */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={back}
          disabled={step === 0}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
        >
          <ArrowLeft className="h-4 w-4" /> Tillbaka
        </button>
        <Button variant="mint" arrow onClick={next} disabled={!canAdvance}>
          {step === TOTAL_STEPS - 1 ? "Visa rekommendation" : "Nästa"}
        </Button>
      </div>
    </div>
  );
}

function Choice<T extends string>({
  heading,
  options,
  value,
  onSelect,
}: {
  heading: string;
  options: { id: T; label: string; help: string }[];
  value: T | null;
  onSelect: (v: T) => void;
}) {
  return (
    <div>
      <h3 className="text-xl font-semibold md:text-2xl">{heading}</h3>
      <div className="mt-6 space-y-3">
        {options.map((o) => {
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelect(o.id)}
              className={`flex w-full items-start gap-3 rounded-2xl border px-5 py-4 text-left transition-colors ${
                active
                  ? "border-mint bg-mint/10"
                  : "border-white/15 bg-background/40 hover:border-white/35"
              }`}
            >
              <span
                className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border ${
                  active ? "border-mint bg-mint text-mint-foreground" : "border-white/30"
                }`}
              >
                {active && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span>
                <span className="block text-sm font-semibold md:text-base">{o.label}</span>
                <span className="mt-0.5 block text-sm text-muted-foreground">{o.help}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Result({ rec, onReset }: { rec: Recommendation; onReset: () => void }) {
  const content =
    rec.track === "pulse"
      ? {
          eyebrow: "Vår rekommendation",
          title: PACKAGES[rec.packageId].name,
          body: PACKAGES[rec.packageId].desc,
          price: `≈ ${formatCurrency(rec.monthly)} kr/mån`,
          priceNote: "Uppskattat månadspris utifrån era svar. Exakt nivå sätter vi på mötet.",
          cta: { label: "Boka strategimöte", to: "/kontakt", trackId: "price_finder_recommendation_cta" as const },
          secondary: { label: "Se alla priser", to: "/priser" },
        }
      : rec.track === "prime"
        ? {
            eyebrow: "Vår rekommendation",
            title: "Prime — projektproduktion",
            body: "Ni har ett specifikt produktionsbehov. Prime är vår projektmodell för kampanjer, brandfilmer och större produktioner med dedikerat team.",
            price: "Offert per projekt",
            priceNote: "Vi räknar fram ett fast pris utifrån omfattning — inga dolda påslag.",
            cta: { label: "Be om offert", to: "/kontakt", trackId: "price_finder_recommendation_cta" as const },
            secondary: { label: "Läs om Prime", to: "/prime" },
          }
        : {
            eyebrow: "Vår rekommendation",
            title: "Pilot — testa oss först",
            body: "Ni vill se hur vi jobbar innan ni binder er. Pilot är en lågtröskel-ingång: en avgränsad första produktion som visar tempo och kvalitet — utan långt avtal.",
            price: "Lågtröskel-start",
            priceNote: "En fast, avgränsad produktion. Gillar ni det, växlar vi upp till Pulse.",
            cta: { label: "Starta ett Pilot", to: "/pilot", trackId: "price_finder_recommendation_cta" as const },
            secondary: { label: "Boka möte i stället", to: "/kontakt" },
          };

  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-mint/30 bg-mint/[0.06] p-7 backdrop-blur-md md:p-9">
      <div className="text-xs uppercase tracking-[0.18em] text-mint">{content.eyebrow}</div>
      <h3 className="mt-3 text-3xl font-semibold md:text-4xl">{content.title}</h3>
      <p className="mt-4 text-pretty text-base leading-relaxed text-foreground/85">{content.body}</p>

      <div className="mt-7 rounded-2xl border border-white/10 bg-background/40 p-5">
        <div className="text-2xl font-semibold text-mint md:text-3xl">{content.price}</div>
        <p className="mt-1 text-sm text-muted-foreground">{content.priceNote}</p>
      </div>

      <div className="mt-7 flex flex-wrap gap-3">
        <Button
          to={content.cta.to}
          variant="mint"
          arrow
          trackId={content.cta.trackId}
          trackProps={{ location: "price_finder_result", label: content.cta.label, recommendation: rec.track }}
        >
          {content.cta.label}
        </Button>
        <Button to={content.secondary.to} variant="outline">
          {content.secondary.label}
        </Button>
      </div>

      <button
        type="button"
        onClick={() => {
          trackCta("open_price_finder", { location: "price_finder_result", label: "Gör om" });
          onReset();
        }}
        className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <RotateCcw className="h-3.5 w-3.5" /> Gör om beräkningen
      </button>
    </div>
  );
}
